import { allOf, anyOf, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The delinquency follow-up: check in with a resident about a past-due
 * balance, ask what made the payment difficult and when they expect to pay,
 * then answer their questions from the knowledge base. A dispute is noted and
 * handed to the team. The agent cannot transfer the call, so a request for a
 * person goes to its own node, which takes a message.
 */
const START_ID = '00000000-0000-4000-8000-00000002d001';
const GREET_ID = '00000000-0000-4000-8000-00000002a001';
const CHECK_IN_ID = '00000000-0000-4000-8000-00000002c001';
const MESSAGE_ID = '00000000-0000-4000-8000-00000002c002';
const END_CLOSE_ID = '00000000-0000-4000-8000-00000002a002';
const END_DISPUTE_ID = '00000000-0000-4000-8000-00000002a003';
const END_DNC_ID = '00000000-0000-4000-8000-00000002a004';
const edgeId = (n) => `00000000-0000-4000-8000-00000002e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(10),
    toNodeId: GREET_ID,
});
export const DELINQUENCY_GRAPH_TEMPLATE = {
    name: 'Delinquency follow-up',
    persona: 'You are Sam, a professional assistant for Super. Stay calm, ' +
        'respectful and non-judgmental. One or two short spoken sentences per ' +
        'turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: "Hello {{firstName}}, this is Sam with Super. I'm calling about a " +
                "past-due balance on your rent account. I'd like to take a moment " +
                'to note a few details for our team.',
        },
        {
            id: CHECK_IN_ID,
            type: 'conversation',
            title: 'Payment check-in',
            position: 2,
            prompt: 'Open with the first question below, then ask the second, one at ' +
                'a time, and acknowledge each answer:\n\n' +
                '1. "Did you know you were late for this last month\'s rent due ' +
                'date?"\n\n' +
                '2. "By what date do you expect to have the balance paid?"\n\n' +
                'Then ask if they have any questions, and answer from the knowledge ' +
                'base. If they ask for the exact amount, refer them to their tenant ' +
                'portal. Never threaten, never discuss eviction, and never promise ' +
                'to waive fees or accept partial payments; say a team member will ' +
                'review.',
            tools: [{ tool: 'SWITCH_LANGUAGE' }],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            signals: [
                {
                    id: 'disputes_balance',
                    text: 'The caller disputes that the balance is owed',
                },
                {
                    id: 'ready_to_wrap_up',
                    text: 'All three have happened: (1) the agent asked whether the caller ' +
                        "knew last month's rent was late, (2) the agent asked by what " +
                        'date they expect to pay the balance, and (3) after both, the ' +
                        'caller said they have no other questions or are ready to wrap ' +
                        'up. Declining a question counts as answering it.',
                },
                {
                    id: 'needs_to_leave',
                    text: 'The caller says they need to end the call now, such as having to ' +
                        'go, being busy, or saying goodbye.',
                },
            ],
        },
        {
            id: MESSAGE_ID,
            type: 'conversation',
            title: 'Take a message',
            position: 3,
            prompt: 'Say you cannot transfer them right now, but you can take a ' +
                'message for the team to follow up. Take down their message and ' +
                'read it back briefly. Then ask: "Is there anything else I can help ' +
                'with?"',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'message_taken',
                    text: 'The caller gave their message for the team and said there is ' +
                        'nothing else',
                },
            ],
        },
        {
            id: END_CLOSE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 4,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Thank you, {{firstName}}. I've noted your information for our " +
                    'team. Have a good day.',
            },
        },
        {
            id: END_DISPUTE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I've noted that you dispute this balance, and a member " +
                    'of our team will follow up with you directly. Thank you for your ' +
                    'time.',
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 6,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(3),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(4),
            fromNodeId: null,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        { ...OPENING.edges[1], position: 3 },
        {
            id: edgeId(2),
            fromNodeId: GREET_ID,
            toNodeId: CHECK_IN_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 4,
        },
        // A dispute wins the turn over a wrap-up read in the same turn.
        {
            id: edgeId(5),
            fromNodeId: CHECK_IN_ID,
            toNodeId: END_DISPUTE_ID,
            condition: allOf(signalEquals(CHECK_IN_ID, 'disputes_balance', true)),
            position: 5,
        },
        {
            id: edgeId(6),
            fromNodeId: CHECK_IN_ID,
            toNodeId: END_CLOSE_ID,
            condition: anyOf(signalEquals(CHECK_IN_ID, 'ready_to_wrap_up', true), signalEquals(CHECK_IN_ID, 'needs_to_leave', true)),
            position: 6,
        },
        {
            id: edgeId(8),
            fromNodeId: MESSAGE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MESSAGE_ID, 'message_taken', true)),
            position: 7,
        },
    ],
};
//# sourceMappingURL=delinquency-template.js.map